import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { FilterType, GlobalFilterKeyType, SelectionType, DEFAULT_FILTER_ORDER } from 'utils/types';
import { PagesFilters } from 'containers/PageHeader/reducer';
import FilterBar, { FilterBarOptionType } from 'components/Filter/FilterBar';
import { productsClient } from 'apollo';
import {
  GET_PRODUCT_FROM_CACHE_FRAGMENT,
  GET_PRODUCTS_BY_ID,
  GET_STORE_FROM_CACHE_FRAGMENT,
  GET_STORES
} from 'apollo/queries';
import * as actions from 'containers/PageHeader/actions';
import { basicOriginalPagesFiltersSelector } from 'containers/PageHeader/selectors';
import { PreloadedType } from 'containers/PageHeader/FilterModal';
import { ApolloQueryResult } from '@apollo/client';
import {
  clientIdSelector,
  currentBrandSelector,
  isEnterpriseContextSelector
} from 'store/global/selectors';
import _ from 'lodash';

interface Props {
  clientId: string;
  currentBrand: string;
  globalFilterContext: GlobalFilterKeyType;
  pagesFilter: PagesFilters<FilterType>;
  typesPreloaded?: PreloadedType;
  isEnterpriseContext: boolean;
  onClearAll(globalFilterKey: GlobalFilterKeyType, typesPreloaded: PreloadedType): void;
  onItemRemove(globalFilterKey: GlobalFilterKeyType, type: string, id: string): void;
}

const FilterBarContainer: React.FC<Props> = ({
  clientId,
  currentBrand,
  globalFilterContext,
  pagesFilter,
  typesPreloaded = { accounts: false, items: false, stores: false, states: false },
  isEnterpriseContext,
  onClearAll,
  onItemRemove
}) => {
  const location = useLocation();
  const globalFilter = pagesFilter[globalFilterContext];
  const [items, setItems] = useState<Array<FilterBarOptionType>>([]);
  const [loadingItems, setLoadingItems] = useState<boolean>(false);
  const [loadingStores, setLoadingStores] = useState<boolean>(false);
  useEffect(() => {
    let result: Array<FilterBarOptionType> = [];
    /* In this effect, we will add accounts and states to the items to show, and then load info
    for items and stores from cache (or backend, in case is not present on the cache) to add them
    to the items to show.
    By the new requirements the preloaded (unclearable) items should not be shown in the filter bar
    and we should use the breadcrumb as an indication for these filters.
    */
    const queryStore = (tags: string, tagName: string) => {
      productsClient
        .query({
          query: GET_STORES,
          variables: {
            clientId: clientId,
            [tagName]: tags
          },
          fetchPolicy: 'cache-first'
        })
        .then((result: ApolloQueryResult<any>) => {
          setLoadingStores(result.loading);
          if (!result.errors) {
            setItems(prevItems =>
              prevItems.concat(
                result.data.getStore.map(store => ({
                  id: store[tagName],
                  label: store.name,
                  type: 'stores',
                  removable: !typesPreloaded.stores
                }))
              )
            );
          }
        })
        .catch(e => console.log(e));
    };
    // For accounts is not needed to read the cache
    if (
      !typesPreloaded.accounts ||
      (location.pathname !== '/details' && location.pathname !== '/voids')
    ) {
      result = result.concat(
        globalFilter.accounts.map(account => ({
          id: account,
          label: account,
          type: 'accounts',
          removable: !typesPreloaded.accounts
        }))
      );
    }
    // For states is not needed to read the cache
    if (
      !typesPreloaded.states ||
      (location.pathname !== '/details' && location.pathname !== '/voids')
    ) {
      result = result.concat(
        globalFilter.states.map(state => ({
          id: state,
          label: state,
          type: 'states',
          removable: !typesPreloaded.states
        }))
      );
    }
    // For each item, get info from cache or add to a list to request from backend later
    const itemsToLoad = []; // list of items not present on the cache, we have to get them from backend
    if (
      !typesPreloaded.items ||
      (location.pathname !== '/details' && location.pathname !== '/voids')
    ) {
      globalFilter.items.forEach(itemId => {
        const product = productsClient.readFragment({
          id: itemId,
          fragment: GET_PRODUCT_FROM_CACHE_FRAGMENT
        });
        if (product === null) {
          itemsToLoad.push(itemId);
        } else {
          result.push({
            id: itemId,
            // @ts-ignore
            label: product ? product.name : itemId,
            type: 'items',
            removable: !typesPreloaded.items
          });
        }
      });
    }
    // For each store, get info from cache or add to a list to request from backend later
    const storesToLoad = []; // list of stores not present on the cache, we have to get them from backend
    if (
      !typesPreloaded.stores ||
      (location.pathname !== '/details' && location.pathname !== '/voids')
    ) {
      globalFilter.stores.forEach(storeId => {
        const store = productsClient.readFragment({
          id: storeId,
          fragment: GET_STORE_FROM_CACHE_FRAGMENT
        });
        if (store === null) {
          storesToLoad.push(storeId);
        } else {
          result.push({
            id: storeId,
            // @ts-ignore
            label: store ? store.name : storeId,
            type: 'stores',
            removable: !typesPreloaded.stores
          });
        }
      });
    }
    if (itemsToLoad.length > 0) {
      // In case some items are not on the cache, load info from backend
      setLoadingItems(true);
      productsClient
        .query({
          query: GET_PRODUCTS_BY_ID,
          variables: {
            clientId: clientId,
            // The service expects the id, which is the UPC without starting '00'
            ids: itemsToLoad.map(item => item.slice(2)),
            ...(isEnterpriseContext && { brand: currentBrand })
          },
          fetchPolicy: 'cache-first'
        })
        .then((result: ApolloQueryResult<any>) => {
          setLoadingItems(result.loading);
          if (!result.errors) {
            setItems(prevItems =>
              prevItems.concat(
                result.data.batchListProducts?.items.map(product => ({
                  id: `00${product.id}`,
                  label: product.description,
                  type: 'items',
                  removable: !typesPreloaded.items
                })) ?? []
              )
            );
          }
        })
        .catch(e => console.log(e));
    }
    if (storesToLoad.length > 0) {
      // In case some stores are not on the cache, load info from backend
      setLoadingStores(true);
      queryStore(storesToLoad.join(','), 'storeTag');
    }
    setItems(result);
  }, [
    clientId,
    currentBrand,
    globalFilter,
    isEnterpriseContext,
    location.pathname,
    typesPreloaded.accounts,
    typesPreloaded.items,
    typesPreloaded.states,
    typesPreloaded.stores
  ]);

  const handleItemClick = (id: string, type: string) => {
    onItemRemove(globalFilterContext, type, id);
  };

  const hasItems = () => {
    return location.pathname === '/details' ||
      (location.pathname === '/voids' && location.search !== '')
      ? (globalFilter.accounts.length > 0 && !typesPreloaded.accounts) ||
          (globalFilter.items.length > 0 && !typesPreloaded.items) ||
          (globalFilter.stores.length > 0 && !typesPreloaded.stores) ||
          (globalFilter.states.length > 0 && !typesPreloaded.states)
      : globalFilter.accounts.length > 0 ||
          globalFilter.items.length > 0 ||
          globalFilter.stores.length > 0 ||
          globalFilter.states.length > 0;
  };

  if (hasItems()) {
    return (
      <Row className="mt-3">
        <Col>
          <FilterBar
            items={_.sortBy(items, function(obj) {
              return DEFAULT_FILTER_ORDER.indexOf(obj.type);
            })}
            loading={loadingItems || loadingStores}
            onClearAllClick={() => onClearAll(globalFilterContext, typesPreloaded)}
            onItemClick={handleItemClick}
          />
        </Col>
      </Row>
    );
  }
  return null;
};

const mapStateToProps = state => ({
  clientId: clientIdSelector(state),
  currentBrand: currentBrandSelector(state),
  pagesFilter: basicOriginalPagesFiltersSelector(state),
  isEnterpriseContext: isEnterpriseContextSelector(state)
});

const mapDispatchToProps = dispatch => ({
  onClearAll: (globalFilterKey: GlobalFilterKeyType, typesPreloaded: PreloadedType) => {
    Object.keys(typesPreloaded).forEach(type => {
      if (!typesPreloaded[type]) {
        dispatch(actions.clearGlobalFilterForType(globalFilterKey, type as SelectionType));
      }
    });
  },
  onItemRemove: (globalFilterKey: GlobalFilterKeyType, type: string, id: string) => {
    dispatch(actions.removeGlobalFilterItem(globalFilterKey, type, id));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(FilterBarContainer);
